import { cn } from "src/utils/cn";

type Page = {
  page: number;
  type: "next" | "prev" | "page";
  position: "first" | "middle" | "last" | "alone";
  isActive: boolean;
};

// exported just for testing
export function getPages({
  activePage,
  totalPages,
  maxPagesDisplayed,
}: {
  activePage: number;
  totalPages: number;
  maxPagesDisplayed: number;
}): Page[] {
  const numPagesDisplayedOtherThanCurrent = Math.max(0, maxPagesDisplayed - 1);
  const isActivePageFirst = activePage === 1;
  const isActivePageLast = activePage === totalPages;

  const firstPage = isActivePageFirst
    ? 1
    : isActivePageLast
      ? Math.max(1, totalPages - numPagesDisplayedOtherThanCurrent)
      : Math.max(
          1,
          Math.floor(activePage - numPagesDisplayedOtherThanCurrent / 2)
        );

  const lastPage = Math.min(
    totalPages,
    firstPage + numPagesDisplayedOtherThanCurrent
  );

  const isShowPrevious = activePage > 1;
  const isShowNext = activePage < totalPages;
  const isAlonePage = totalPages === 1;

  const pages: Page[] = [];

  if (isAlonePage) {
    return [
      {
        page: activePage,
        type: "page",
        position: "alone",
        isActive: true,
      },
    ];
  }

  if (isShowPrevious) {
    pages.push({
      page: activePage - 1,
      type: "prev",
      position: "first",
      isActive: false,
    });
  }

  for (let i = firstPage; i <= lastPage; i++) {
    const position =
      !isShowPrevious && i === firstPage
        ? "first"
        : !isShowNext && i === lastPage
          ? "last"
          : "middle";

    pages.push({
      page: i,
      type: "page",
      position,
      isActive: i === activePage,
    });
  }

  if (isShowNext) {
    pages.push({
      page: activePage + 1,
      type: "next",
      position: "last",
      isActive: false,
    });
  }

  return pages;
}

type GroupVideoListPaginationProps = {
  page: number;
  totalPages: number;
  /** The max number of page buttons to display, not including the next/prev
   * buttons. */
  maxPagesDisplayed: number;
  onPageChange: (page: number) => void;
  previousLabel?: string;
  nextLabel?: string;
};

export function GroupVideoListPagination({
  page,
  totalPages,
  maxPagesDisplayed,
  onPageChange,
  previousLabel = "prev",
  nextLabel = "next",
}: GroupVideoListPaginationProps) {
  const pages = getPages({
    activePage: page,
    totalPages,
    maxPagesDisplayed,
  });

  return (
    <div>
      {pages.map((page, index) => {
        return (
          <Page
            key={index}
            position={page.position}
            onClick={() => onPageChange(page.page)}
            isActive={page.isActive}
          >
            {page.type === "prev"
              ? previousLabel
              : page.type === "next"
                ? nextLabel
                : page.page}
          </Page>
        );
      })}
    </div>
  );
}

function Page({
  isActive = false,
  onClick,
  position,
  children,
}: {
  isActive?: boolean;
  onClick: () => void;
  position: "first" | "middle" | "last" | "alone";
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "border-y border-r border-gray-100 px-4 py-2",
        isActive
          ? "border-red-700 bg-red-700 text-white"
          : "border-gray-100 bg-white text-red-700",
        (position === "first" || position === "alone") &&
          "rounded-l-md border-l",
        (position === "last" || position === "alone") && "rounded-r-md"
      )}
    >
      {children}
    </button>
  );
}
